// @flow
import {
  Button, Checkbox, CircularProgress, Container, CssBaseline,
  FormControlLabel, Grid, TextField, Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Formik } from 'formik';
import { useSnackbar } from 'notistack';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import routes from '../routes';
import { AuthContext } from '../context/auth';
import { isNotValidEmail, isNotValidName, isNotValidPassword, isNotValidPhone } from '../util/validator';
import ApiResponse from '../model/ApiResponse';
import Image from './signup-background.jpg';

const baseUrl = process.env.PUBLIC_URL || '';
const privacyPolicyUrl: string = String(process.env.REACT_APP_PRIVACY_POLICY_URL);
const marketingCampaignUrl: string = String(process.env.REACT_APP_MARKETING_CAMPAIGN_URL);

const useStyles = makeStyles((theme) => ({
  background: {
    background: `url(${Image}) no-repeat center center fixed`,
    '-webkit-background-size': 'cover',
    '-moz-background-size': 'cover',
    '-o-background-size': 'cover',
    backgroundSize: 'cover',
    width: '100%',
    minHeight: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(2),
  },
  paper: {
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    backgroundColor: 'white',
    borderRadius: theme.spacing(1),
    boxShadow: theme.shadows[5],
  },
  form: {
    width: '100%', // Fix IE 11 issue.
    marginTop: theme.spacing(3),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
  error: {
    color: theme.palette.error.main,
  },
}));

type Props = {
  provider: string,
};

const SignUp = (props: Props) => {
  const { t } = useTranslation();
  const { doPost, setToken } = useContext(AuthContext);
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();
  const { provider } = props;

  const validate = values => {
    const errors = {};
    if (!values.name) {
      errors.name = t('Required');
    } else if (isNotValidName(values.name)) {
      errors.name = t('Invalid name');
    }
    if (!values.email) {
      errors.email = t('Required');
    } else if (isNotValidEmail(values.email)) {
      errors.email = t('Invalid email address');
    }
    if (!values.phone) {
      errors.phone = t('Required');
    } else if (isNotValidPhone(values.phone)) {
      errors.phone = t('Invalid phone');
    }
    if (!values.password) {
      errors.password = t('Required');
    } else if (isNotValidPassword(values.password)) {
      errors.password = t('Invalid password');
    }
    if (!values.policy) {
      errors.policy = t('Required');
    }
    return errors;
  };

  const onSubmit = async (values, { setSubmitting }) => {
    const data = {
      ...values,
      privacyPolicy: values.policy,
      marketingCampaign: values.marketing,
      deviceData: navigator.userAgent,
      provider,
    };
    const response: ApiResponse = await doPost('/customer/register', data);
    if (response.ok && response.data) {
      await setToken(response.data);
    } else {
      const errorMessage = response.message || 'error.unknown';
      enqueueSnackbar(t(errorMessage, errorMessage), {
        variant: 'error',
      });
      setSubmitting(false);
    }
  };

  const theForm = (
    <Container component="main" maxWidth="sm">
      <CssBaseline />
      <div className={classes.paper}>
        <img src={`${baseUrl}/images/logo.png`} alt="" width={40} height={40} />
        <Typography component="h1" variant="h5">
          {t('Enter your information')}
        </Typography>
        <Formik
          initialValues={{
            name: '',
            email: '',
            phone: '',
            password: '',
            policy: false,
            marketing: false,
            deviceData: 'TODO',
          }}
          validate={validate}
          onSubmit={onSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
            setFieldValue,
          }) => (
            <form className={classes.form} onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    disabled={isSubmitting}
                    autoComplete="name"
                    name="name"
                    variant="outlined"
                    required
                    fullWidth
                    id="name"
                    label={t('Full name')}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.name}
                  />
                  <span className={classes.error}>{errors.name && touched.name && errors.name}</span>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    disabled={isSubmitting}
                    variant="outlined"
                    required
                    fullWidth
                    id="email"
                    label={t('Email')}
                    name="email"
                    autoComplete="email"
                    type="email"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.email}
                  />
                  <span className={classes.error}>{errors.email && touched.email && errors.email}</span>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    disabled={isSubmitting}
                    variant="outlined"
                    required
                    fullWidth
                    id="phone"
                    label={t('Phone')}
                    name="phone"
                    autoComplete="phone"
                    type="tel"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.phone}
                  />
                  <span className={classes.error}>{errors.phone && touched.phone && errors.phone}</span>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    disabled={isSubmitting}
                    variant="outlined"
                    required
                    fullWidth
                    name="password"
                    label={t('Password')}
                    type="password"
                    id="password"
                    autoComplete="current-password"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.password}
                  />
                  <span className={classes.error}>{errors.password && touched.password && errors.password}</span>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    disabled={isSubmitting}
                    autoComplete="retailCode"
                    name="retailCode"
                    variant="outlined"
                    fullWidth
                    id="retailCode"
                    label={<span>
                      {t('Reference code')}&nbsp;<small><i>{`(${t('Optional')})`}</i></small>
                    </span>}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.retailCode}
                  />
                  <span className={classes.error}>{errors.retailCode && touched.retailCode && errors.retailCode}</span>
                </Grid>
                <Grid item xs={12} sm={12} md={12} lg={12}>
                  <FormControlLabel
                    control={<Checkbox
                      disabled={isSubmitting}
                      checked={values.policy}
                      onChange={(e) => setFieldValue('policy', e.target.checked)}
                      name='policy'
                      color='primary' />}
                    label={<a href={privacyPolicyUrl} target='_blank' rel='noopener noreferrer'>
                      <small>{t('Accept privacy policy')}</small>
                    </a>} />
                  <span className={classes.error}>{errors.policy && touched.policy && errors.policy}</span>
                </Grid>
                <Grid item xs={12} sm={12} md={12} lg={12}>
                  <FormControlLabel
                    control={<Checkbox
                      disabled={isSubmitting}
                      checked={values.marketing}
                      onChange={(e) => setFieldValue('marketing', e.target.checked)}
                      name='marketing'
                      color='primary' />}
                    label={<a href={marketingCampaignUrl} target='_blank' rel='noopener noreferrer'>
                      <small>{t('Accept marketing campaigns')}</small>
                    </a>} />
                  <span className={classes.error}>{errors.marketing && touched.marketing && errors.marketing}</span>
                </Grid>
              </Grid>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                className={classes.submit}
                type="submit"
                disabled={isSubmitting}
                startIcon={isSubmitting ? <CircularProgress color="secondary" size="1.2em" /> : null}
              >
                {t('Next')}
              </Button>
              <Grid container justify="flex-end">
                <Grid item>
                  {!isSubmitting && <Link to={routes.signIn}>
                    {t('Already a customer? Sign in')}
                  </Link>}
                </Grid>
              </Grid>
            </form>
          )}
        </Formik>
      </div>
    </Container>
  );

  return (
    <div className={classes.background}>
      {theForm}
    </div>
  );
};

export default SignUp;
